// Ported from design/project/App58-PlayLimits.dc.html and App58b-BreakConfirm.dc.html, then wired: the player's own
// daily stake limit and breaks. Lowering works at once, raising waits 24 hours; a break can't be ended early.
import { useEffect, useState } from "react";
import { api, ApiError } from "../api.js";
import { useGame } from "../game.js";
import { go } from "../router.js";

export interface LimitsView {
  daily: { limit: number | null; staked: string; resetsAt: number; pending: { limit: number | null; at: number } | null };
  break: { until: number; byStaff: boolean } | null;
  choices: number[];
  breakDays: number[];
}

const when = (ms: number) => new Date(ms).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos" });
const dayName = (ms: number) => new Date(ms).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos" });
const BREAK_LABEL: Record<number, string> = { 1: "24 hours", 7: "7 days", 30: "30 days" };

function Chip({ on, children, onClick, disabled }: { on?: boolean; children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} className="mono" style={{
      height: "40px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "700", fontSize: "14px", cursor: "pointer", font: "inherit",
      ...(on ? { background: "rgba(204,255,0,0.14)", border: "1px solid #CCFF00", color: "#CCFF00" } : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#FFFFFF" }),
      opacity: disabled ? 0.45 : 1,
    }}>{children}</button>
  );
}

export function App58_PlayLimits() {
  const { me, refresh } = useGame();
  const [view, setView] = useState<LimitsView | null>(null);
  const [pick, setPick] = useState<number | null | undefined>(undefined);
  const [confirmDays, setConfirmDays] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    void api<LimitsView>("/v1/limits").then((v) => { setView(v); setPick(v.daily.limit); }).catch(() => setNote("Couldn't load your limits. Try again."));
  }, []);

  const save = async () => {
    if (!view || pick === undefined) return;
    setBusy(true);
    try {
      const r = await api<LimitsView & { when: "now" | "later" }>("/v1/limits", { method: "POST", body: { daily: pick } });
      setView(r);
      setNote(r.when === "now" ? (pick === null ? "No daily limit." : `Your daily limit is ${pick} USDT now.`) : `Saved. It changes on ${dayName(r.daily.pending!.at)} WAT.`);
    } catch {
      setNote("That didn't save. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const startBreak = async (days: number) => {
    setBusy(true);
    try {
      const r = await api<LimitsView>("/v1/break", { method: "POST", body: { days } });
      setView(r);
      setConfirmDays(null);
      void refresh().catch(() => {});
      go("App54", true);
    } catch (e) {
      setNote(e instanceof ApiError && e.code === "already_on_break" ? "You're already on a break." : "That didn't work. Try again.");
      setConfirmDays(null);
    } finally {
      setBusy(false);
    }
  };

  const d = view?.daily;
  const pct = d && d.limit ? Math.min(100, (Number(d.staked) / d.limit) * 100) : 0;
  const changed = view !== null && pick !== undefined && pick !== view.daily.limit;
  const onBreak = !!view?.break;
  return (
    <>
      <div className="screen">
        <div className="orbR" aria-hidden="true" style={{ top: "40px", right: "-200px", width: "460px", height: "460px" }}></div>
        <div className="pad squeeze" style={{ height: "100%", paddingBottom: "calc(var(--dd-safe-bottom, 0px) + 20px)", overflowY: "auto" }}>
          <div className="row" style={{ marginTop: "6px", flexShrink: 0 }}>
            <button onClick={() => go("App24", true)} className="ib" aria-label="Back"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"></path></svg></button>
            <span className="h2" style={{ flex: "1", textAlign: "center", marginRight: "36px" }}>Play limits</span>
          </div>

          <span className="lbl" style={{ marginTop: "16px", fontSize: "10px", flexShrink: 0 }}>DAILY STAKE LIMIT</span>
          <div className="g" style={{ marginTop: "8px", borderRadius: "20px", padding: "14px", flexShrink: 0 }}>
            <div className="row"><span className="muted" style={{ fontSize: "13px" }}>Staked today</span><span className="mono" style={{ fontWeight: "800", fontSize: "15px" }}>{d ? Number(d.staked).toLocaleString("en-US") : "..."} <span className="muted" style={{ fontWeight: "500" }}>/ {d?.limit ? `${d.limit} USDT` : "no limit"}</span></span></div>
            <div style={{ marginTop: "10px", height: "6px", borderRadius: "3px", background: "#30363D", overflow: "hidden" }}><div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "#FF3366" : "#CCFF00" }}></div></div>
            <div style={{ marginTop: "14px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
              {(view?.choices ?? [25, 50, 100, 250, 500]).map((c) => <Chip key={c} on={pick === c} onClick={() => setPick(c)} disabled={!view}>{c}</Chip>)}
              <Chip on={pick === null} onClick={() => setPick(null)} disabled={!view}>No limit</Chip>
            </div>
            {d?.pending && (
              <div style={{ marginTop: "12px", display: "flex", gap: "8px", alignItems: "flex-start", padding: "10px 12px", borderRadius: "12px", background: "rgba(255,194,77,0.08)", border: "1px solid rgba(255,194,77,0.3)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFC24D" strokeWidth="2.4" strokeLinecap="round" style={{ flex: "none", marginTop: "2px" }} aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>
                <span style={{ fontSize: "12px", lineHeight: "17px", color: "#C9D1D9" }}>{d.pending.limit === null ? "Turning off" : `Going up to ${d.pending.limit} USDT`} on {dayName(d.pending.at)}. Raising takes 24 hours.</span>
              </div>
            )}
            <span className="muted" style={{ display: "block", marginTop: "10px", fontSize: "11px", lineHeight: "16px" }}>Lowering works at once. Raising it or turning it off waits 24 hours. Resets at 00:00 WAT.</span>
          </div>

          <span className="lbl" style={{ marginTop: "16px", fontSize: "10px", flexShrink: 0 }}>TAKE A BREAK</span>
          <div className="g" style={{ marginTop: "8px", borderRadius: "20px", padding: "14px", flexShrink: 0 }}>
            {onBreak ? (
              <span style={{ fontSize: "13px", lineHeight: "19px" }}>You're on a break until {when(view!.break!.until)} WAT. Withdrawals always work.</span>
            ) : (
              <>
                <span style={{ fontSize: "13px", lineHeight: "19px" }}>No battles or deposits until the break ends. Withdrawals always work.</span>
                <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px" }}>
                  {(view?.breakDays ?? [1, 7, 30]).map((days) => <Chip key={days} onClick={() => setConfirmDays(days)} disabled={!view || busy}>{BREAK_LABEL[days] ?? `${days} days`}</Chip>)}
                </div>
                <span className="muted" style={{ display: "block", marginTop: "10px", fontSize: "11px" }}>A break can't be ended early.</span>
              </>
            )}
          </div>

          {note && <span role="status" style={{ marginTop: "12px", fontSize: "13px", textAlign: "center", flexShrink: 0 }}>{note}</span>}
          <div style={{ marginTop: "auto", paddingTop: "16px", display: "flex", flexDirection: "column", gap: "10px", flexShrink: 0 }}>
            <button className="btnP" disabled={!changed || busy} onClick={() => void save()} style={{ opacity: changed ? 1 : 0.45 }}>{busy ? "SAVING..." : "SAVE LIMIT"}</button>
          </div>
        </div>
      </div>

      {confirmDays !== null && (
        <div role="dialog" aria-modal="true" aria-label="Take a break" onClick={() => !busy && setConfirmDays(null)} style={{ position: "fixed", inset: "0", zIndex: 30, background: "rgba(11,14,20,0.82)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ boxSizing: "border-box", width: "100%", maxWidth: "480px", alignSelf: "center", padding: "18px 20px calc(var(--tg-safe-area-inset-bottom, 34px) + var(--tg-content-safe-area-inset-bottom, 0px) + 12px)", borderRadius: "26px 26px 0 0", background: "rgba(18,22,29,0.97)", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif", color: "#FFFFFF" }}>
            <div style={{ alignSelf: "center", width: "40px", height: "4px", borderRadius: "2px", background: "#30363D", marginBottom: "14px" }}></div>
            <div style={{ display: "flex", justifyContent: "center" }}><div style={{ width: "64px", height: "64px", borderRadius: "20px", background: "rgba(138,124,255,0.12)", border: "1px solid rgba(138,124,255,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8A7CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"></path></svg></div></div>
            <span className="h2" style={{ marginTop: "14px", textAlign: "center" }}>Take a {BREAK_LABEL[confirmDays]?.replace("24 hours", "24 hour").replace(" days", " day") ?? confirmDays} break?</span>
            <p className="muted" style={{ margin: "8px 4px 0", textAlign: "center", fontSize: "13px", lineHeight: "19px" }}>
              No battles or deposits until {when(Date.now() + confirmDays * 86_400_000)} WAT. You can't end it early. Your {me?.balance.available ?? "..."} USDT stays yours and you can withdraw any time.
            </p>
            <button className="btnP" disabled={busy} onClick={() => void startBreak(confirmDays)} style={{ marginTop: "18px", background: "#8A7CFF", boxShadow: "0 0 24px rgba(138,124,255,0.35)" }}>{busy ? "STARTING..." : "START MY BREAK"}</button>
            <button className="btnS" disabled={busy} onClick={() => setConfirmDays(null)} style={{ marginTop: "10px" }}>Not now</button>
          </div>
        </div>
      )}
    </>
  );
}
